import { fail, success } from '@helpers/result-helper.js'
import { isHttpStatus, isSuccess } from '@/helpers/http-status-helpers'

export default {
  namespaced: true,
  state: {
    candidateDisplayPictures: [],
    displayPictureLoaders: [],
    displayPictureRequestQueue: [],
    maxConcurrentDisplayPictureRequests: 2,
    activeDisplayPictureRequests: 0,
    activeRequestControllers: new Map(), // Track AbortControllers
  },
  getters: {
    isDisplayPictureLoading: (state) => (candidateId) =>
      state.displayPictureLoaders.includes(candidateId),
    cachedDisplayPictureRecord: (state) => (candidateId) => {
      const found = state.candidateDisplayPictures.find(
        (pic) => pic.candidateId === candidateId
      )
      return found ?? null
    },
    candidateDisplayPicture: (state, getters) => (candidateId) => {
      const cachedDisplayPictureRecord = getters.cachedDisplayPictureRecord(
        candidateId
      )
      return cachedDisplayPictureRecord
        ? cachedDisplayPictureRecord.image
        : null
    },
  },
  mutations: {
    START_LOADING_DISPLAY_PICTURE(state, candidateId) {
      state.displayPictureLoaders.push(candidateId)
    },
    FINISH_LOADING_DISPLAY_PICTURE(state, candidateId) {
      const index = state.displayPictureLoaders.indexOf(candidateId)
      if (index !== -1) state.displayPictureLoaders.splice(index, 1)
    },
    SET_DISPLAY_PICTURE(state, data) {
      const existingDisplayPicture = state.candidateDisplayPictures.find(
        (pic) => pic.candidateId === data.candidateId
      )

      if (existingDisplayPicture) {
        existingDisplayPicture.image = data.image
      } else {
        state.candidateDisplayPictures.push(data)
      }
    },
    INCREMENT_ACTIVE_DISPLAY_PICTURE_REQUESTS(state) {
      state.activeDisplayPictureRequests++
    },
    DECREMENT_ACTIVE_DISPLAY_PICTURE_REQUESTS(state) {
      state.activeDisplayPictureRequests--
    },
    ADD_TO_DISPLAY_PICTURE_QUEUE(
      state,
      { candidateId, resolve, reject, abortController }
    ) {
      state.displayPictureRequestQueue.push({
        candidateId,
        resolve,
        reject,
        abortController,
      })
    },
    REMOVE_FROM_DISPLAY_PICTURE_QUEUE(state, candidateId) {
      state.displayPictureRequestQueue = state.displayPictureRequestQueue.filter(
        (item) => item.candidateId !== candidateId
      )
    },
    SHIFT_FROM_DISPLAY_PICTURE_QUEUE(state) {
      state.displayPictureRequestQueue.shift()
    },
    REGISTER_REQUEST_CONTROLLER(state, { candidateId, abortController }) {
      state.activeRequestControllers.set(candidateId, abortController)
    },
    UNREGISTER_REQUEST_CONTROLLER(state, candidateId) {
      state.activeRequestControllers.delete(candidateId)
    },
    CLEAR_DISPLAY_PICTURE_QUEUE(state) {
      // Cancel all queued requests
      state.displayPictureRequestQueue.forEach(
        ({ abortController, reject }) => {
          if (abortController && !abortController.signal.aborted) {
            abortController.abort()
          }
          // Reject the promise to clean up (silently - this is expected behavior)
          try {
            // Use a special error that won't be logged as an unhandled rejection
            const cancellationError = new Error('Request cancelled')
            cancellationError.name = 'CancellationError'
            cancellationError.isCancellation = true
            reject(cancellationError)
          } catch (e) {
            // Promise may have already been resolved/rejected
          }
        }
      )
      state.displayPictureRequestQueue = []
    },
    CLEAR_ALL_REQUEST_CONTROLLERS(state) {
      state.activeRequestControllers.clear()
    },
    RESET_ACTIVE_DISPLAY_PICTURE_REQUESTS(state) {
      state.activeDisplayPictureRequests = 0
    },
    CLEAR_DISPLAY_PICTURES(state) {
      state.candidateDisplayPictures = []
    },
  },
  actions: {
    async loadCandidateDisplayPicture(
      { commit, getters, state, dispatch },
      candidateId
    ) {
      // Use cached image first
      const pictureExists = getters.cachedDisplayPictureRecord(candidateId)
      if (pictureExists) return success(pictureExists.image)

      // Prevent multiple loads of same image
      if (getters.isDisplayPictureLoading(candidateId)) {
        // Return a promise that resolves when the current request completes
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            const cached = getters.cachedDisplayPictureRecord(candidateId)
            if (cached) {
              clearInterval(checkInterval)
              resolve(success(cached.image))
            }
            // Also check if component was destroyed
            if (
              !state.activeRequestControllers.has(candidateId) &&
              !getters.isDisplayPictureLoading(candidateId)
            ) {
              clearInterval(checkInterval)
              resolve(success(null))
            }
          }, 50)
        })
      }

      // Create AbortController for this request
      const abortController = new AbortController()
      commit('REGISTER_REQUEST_CONTROLLER', { candidateId, abortController })

      // Queue the request if we're at max capacity
      if (
        state.activeDisplayPictureRequests >=
        state.maxConcurrentDisplayPictureRequests
      ) {
        return new Promise((resolve, reject) => {
          // Wrap reject to handle cancellation silently
          const wrappedReject = (error) => {
            // Silently handle cancellation errors
            if (
              error &&
              (error.name === 'CancellationError' || error.isCancellation)
            ) {
              resolve(fail())
            } else {
              reject(error)
            }
          }

          commit('ADD_TO_DISPLAY_PICTURE_QUEUE', {
            candidateId,
            resolve,
            reject: wrappedReject,
            abortController,
          })
          // Process queue when a slot opens
          dispatch('processDisplayPictureQueue')
        })
      }

      return dispatch('_processDisplayPictureRequest', {
        candidateId,
        abortController,
      })
    },

    async _processDisplayPictureRequest(
      { commit, getters, state, dispatch },
      { candidateId, abortController }
    ) {
      try {
        commit('START_LOADING_DISPLAY_PICTURE', candidateId)
        commit('INCREMENT_ACTIVE_DISPLAY_PICTURE_REQUESTS')

        // Check if already aborted before making request
        if (abortController.signal.aborted) {
          return fail()
        }

        // Pass abort signal to API call
        // Axios 0.22.0+ supports AbortSignal natively and will cancel the request
        const response = await this.$api.candidate.getDisplayPic(candidateId, {
          signal: abortController.signal,
        })

        if (isSuccess(response.status)) {
          const imageValue = isHttpStatus(response.status, 'No Content')
            ? null
            : response.data.base64ProfileImg
          commit('SET_DISPLAY_PICTURE', {
            candidateId,
            image: imageValue,
          })
          return success(imageValue)
        }
      } catch (error) {
        // Axios 0.22.0+ will throw AbortError when request is cancelled
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return fail()
        }
        return fail()
      } finally {
        commit('FINISH_LOADING_DISPLAY_PICTURE', candidateId)
        commit('DECREMENT_ACTIVE_DISPLAY_PICTURE_REQUESTS')
        commit('UNREGISTER_REQUEST_CONTROLLER', candidateId)
        // Process next item in queue
        dispatch('processDisplayPictureQueue')
      }
    },

    async processDisplayPictureQueue({ commit, state, dispatch }) {
      if (
        state.activeDisplayPictureRequests >=
        state.maxConcurrentDisplayPictureRequests
      ) {
        return
      }

      if (state.displayPictureRequestQueue.length === 0) {
        return
      }

      // Get the first item before removing it
      const queueItem = state.displayPictureRequestQueue[0]
      const { candidateId, resolve, reject, abortController } = queueItem

      // Remove from queue using mutation
      commit('SHIFT_FROM_DISPLAY_PICTURE_QUEUE')

      // Check if request was already aborted
      if (abortController.signal.aborted) {
        // Reject silently - this is expected when cancelling
        const cancellationError = new Error('Request cancelled')
        cancellationError.name = 'CancellationError'
        cancellationError.isCancellation = true
        reject(cancellationError)
        return
      }

      try {
        const result = await dispatch('_processDisplayPictureRequest', {
          candidateId,
          abortController,
        })
        resolve(result)
      } catch (error) {
        // Silently handle cancellation errors (expected behavior)
        if (
          error.name === 'AbortError' ||
          error.name === 'CancellationError' ||
          error.isCancellation
        ) {
          // Don't reject - just resolve with fail() to prevent unhandled rejection
          resolve(fail())
        } else {
          reject(error)
        }
      }
    },

    cancelDisplayPictureRequest({ commit, state }, candidateId) {
      const abortController = state.activeRequestControllers.get(candidateId)
      if (abortController) {
        abortController.abort()
        commit('FINISH_LOADING_DISPLAY_PICTURE', candidateId)
        commit('UNREGISTER_REQUEST_CONTROLLER', candidateId)
        commit('DECREMENT_ACTIVE_DISPLAY_PICTURE_REQUESTS')
      }
      // Also remove from queue if present
      commit('REMOVE_FROM_DISPLAY_PICTURE_QUEUE', candidateId)
    },

    clearAllDisplayPictureRequests({ commit, state }) {
      // Get a copy of all controllers before iterating to avoid mutating during iteration
      const controllersToCancel = Array.from(
        state.activeRequestControllers.entries()
      )

      // Cancel all active requests
      controllersToCancel.forEach(([candidateId, abortController]) => {
        if (abortController && !abortController.signal.aborted) {
          abortController.abort()
        }
        commit('FINISH_LOADING_DISPLAY_PICTURE', candidateId)
        commit('UNREGISTER_REQUEST_CONTROLLER', candidateId)
      })

      commit('CLEAR_ALL_REQUEST_CONTROLLERS')
      commit('RESET_ACTIVE_DISPLAY_PICTURE_REQUESTS')

      // Clear queue (this will cancel queued requests)
      commit('CLEAR_DISPLAY_PICTURE_QUEUE')
    },

    clear({ commit, dispatch }) {
      dispatch('clearAllDisplayPictureRequests')
      commit('CLEAR_DISPLAY_PICTURES')
    },
  },
}
